// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import { Container } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import DataSlot from 'components/atoms/DataSlot';
import Panel from 'components/atoms/Panel';
import SelfCheckInHeader from 'components/atoms/SelfCheckInHeader/';
import ContactPreference from 'components/molecules/ContactPreference';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Types
import type { PersonInfo } from 'api/xws/login/entities/PersonInfo';
import type { ContactInfo } from 'api/xws/login/entities/ContactInfo';

// utils
import { isPhoneNumberValid } from 'utils/string';

// Constants
import {
  SCI_SERVICES_ROUTE,
  SCI_SIGNATURE_ROUTE,
  WAITING_OPTIONS,
  WAITING_OPTIONS_SWITCH_CONST
} from 'constants/selfCheckIn';
import { NAVIGATION } from 'constants/navigations';
import { labelsStepIndicatorSCI } from 'constants/CTAConfig';

import './NoAdvisorAssistance.scss';

const { NONE, CALL, TEXT, IN_WAITING_AREA } = WAITING_OPTIONS;

const ns = 'no-advisor-assistance';
const { FORWARD } = NAVIGATION;

type State = {
  noNeed: boolean,
  findMe: boolean,
  contactMe: boolean,
  selectedOption: string,
  selectedOptionLabel: string
};

export type Props = {
  intl: IntlShape,
  goTo: (path: string, direction: string) => void,
  dealerCountryCode: string,
  contactInfo: ContactInfo,
  setMobilePhoneNumber: string => void,
  setWaitState: string => void,
  setCustomerMobileNumber: number => void,
  customerMobileNumber: number,
  selfCheckinWaitState: string,
  personInfo: PersonInfo,
  gtmContactPreferenceMethod: (eventResult: string) => void,
  gtmNoAdvisorOptions: (selectedOption: string) => void,
  showSCIProgressBar: boolean
};

class NoAdvisorAssistance extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const call = props.intl.formatMessage({
      id: 'c8.self.check.in.advisor.page.call'
    });
    const text = props.intl.formatMessage({
      id: 'c8.self.check.in.advisor.page.text'
    });
    const isPhoneContactWaitState =
      props.selfCheckinWaitState === CALL ||
      props.selfCheckinWaitState === TEXT;
    this.state = {
      noNeed: props.selfCheckinWaitState === NONE,
      findMe: props.selfCheckinWaitState === IN_WAITING_AREA,
      contactMe: isPhoneContactWaitState,
      selectedOption: isPhoneContactWaitState
        ? props.selfCheckinWaitState
        : CALL,
      selectedOptionLabel: props.selfCheckinWaitState === TEXT ? text : call
    };
  }

  selectOption = (option: string) => {
    const { setWaitState } = this.props;
    const { selectedOption } = this.state;
    this.setState({
      noNeed: false,
      findMe: false,
      contactMe: false
    });
    this.setState({
      [option]: true
    });
    switch (option) {
      case WAITING_OPTIONS_SWITCH_CONST.CONTACT_ME: {
        setWaitState(selectedOption);
        break;
      }
      case WAITING_OPTIONS_SWITCH_CONST.FINDE_ME:
        setWaitState(IN_WAITING_AREA);
        break;
      case WAITING_OPTIONS_SWITCH_CONST.NO_NEED:
      default:
        setWaitState(NONE);
        break;
    }
  };

  onDropdownSelect = (option: string, label: string) => {
    this.setState({
      selectedOption: option,
      selectedOptionLabel: label
    });
  };

  render() {
    // ToDo: remove hardcoded strings and defaultMessages once localization strings are ready and services integrated
    const {
      goTo,
      intl,
      dealerCountryCode,
      contactInfo,
      gtmContactPreferenceMethod,
      gtmNoAdvisorOptions,
      showSCIProgressBar
    } = this.props;
    const {
      noNeed,
      findMe,
      contactMe,
      selectedOptionLabel,
      selectedOption
    } = this.state;
    const notReallyValue = intl.formatMessage({
      id: 'c8.self.check.in.advisor.page.complete.check.in',
      defaultMessage: 'Complete check in'
    });
    const findMeValue = intl.formatMessage({
      id: 'c8.self.check.in.advisor.page.i.will.wait',
      defaultMessage: 'I will wait to speak with an advisor'
    });
    const contactMeValue = intl.formatMessage({
      id: 'c8.self.check.in.advisor.page.advisor.contact.me',
      defaultMessage: 'Have an advisor contact me'
    });
    const mobilePhoneNumber = contactInfo ? contactInfo.mobilePhone : '';
    const enableNextButton =
      noNeed ||
      findMe ||
      (contactMe && isPhoneNumberValid(mobilePhoneNumber, dealerCountryCode));
    let gtmSelectedOption;
    if (noNeed) {
      gtmSelectedOption = 'No need';
    }
    if (findMe) {
      gtmSelectedOption = 'Speak to advisor';
    }
    if (contactMe) {
      gtmSelectedOption = 'Have an advisor contact me';
    }
    return (
      <AppointmentFlowWizard
        isSelfCheckIn={true}
        className={ns}
        showSCIProgressBar={showSCIProgressBar}
        header={() =>
          showSCIProgressBar ? (
            <CtaStepIndicator
              htmlId="VehiclePageStepIndicator"
              labels={labelsStepIndicatorSCI(intl)}
              activeStep={3}
            />
          ) : (
            <SelfCheckInHeader
              label={intl.formatMessage({
                id: 'c8.self.check.in.header.services'
              })}
              route={SCI_SERVICES_ROUTE}
              ariaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.services',
                defaultMessage: 'Back to services'
              })}
            />
          )
        }
        footer={() => (
          <AppointmentFlowFooter
            buttonDisabled={!enableNextButton}
            buttonText={intl.formatMessage({
              id: 'c8.self.check.in.footer.next'
            })}
            onClick={() => {
              gtmNoAdvisorOptions(gtmSelectedOption);
              contactMe && gtmContactPreferenceMethod(selectedOption);
              goTo(SCI_SIGNATURE_ROUTE, FORWARD);
            }}
          />
        )}
        body={() => (
          <React.Fragment>
            {showSCIProgressBar && (
              <SelfCheckInHeader
                label={intl.formatMessage({
                  id: 'c8.self.check.in.header.services'
                })}
                route={SCI_SERVICES_ROUTE}
                ariaLabel={intl.formatMessage({
                  id: 'c8.aria.back.to.services',
                  defaultMessage: 'Back to services'
                })}
              />
            )}
            <Container className={`${ns}__container`}>
              <h1 className={`${ns}__title mb-4`}>
                {intl.formatMessage({
                  id:
                    'c8.self.check.in.advisor.page.do.you.need.to.speak.today',
                  defaultMessage:
                    'Do you need to speak with a service advisor today?'
                })}
              </h1>
              <Panel className={`${ns}__no-panel`}>
                <span className={`${ns}__label`}>
                  {intl.formatMessage({
                    id: 'c8.self.check.in.advisor.page.no.label'
                  })}
                </span>
                <DataSlot
                  value={notReallyValue}
                  bsStyle="primary"
                  onClick={() => {
                    this.selectOption('noNeed');
                  }}
                  active={noNeed}
                />
              </Panel>
              <Panel className={`${ns}__yes-panel`}>
                <div className={`${ns}__label`}>
                  {intl.formatMessage({
                    id: 'c8.self.check.in.advisor.page.yes.label'
                  })}
                </div>
                <DataSlot
                  value={findMeValue}
                  bsStyle="primary"
                  onClick={() => {
                    this.selectOption('findMe');
                  }}
                  active={findMe}
                />
                <DataSlot
                  value={contactMeValue}
                  bsStyle="primary"
                  onClick={() => {
                    this.selectOption('contactMe');
                  }}
                  active={contactMe}
                />
                {contactMe && (
                  <ContactPreference
                    selectedOptionLabel={selectedOptionLabel}
                    onDropdownCallback={this.onDropdownSelect}
                    error={!enableNextButton}
                    uppercaseOptions={true}
                  />
                )}
              </Panel>
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default NoAdvisorAssistance;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/SpeakToAdvisorPage/NoAdvisorAssistance/NoAdvisorAssistance.js